import i18next, {type PostProcessorModule, type TOptions} from 'i18next'
import {getEntityNounMarkerIndex, type MarkerIndexEntry} from './app-config-bridge'
import {createMarkerRegex} from './marker-grammar'

/**
 * Entity-noun marker post-processor (issue 988 / spec §4).
 *
 * <p>Grammar: every `[[content]]` is a marker. Lookup of `content`
 * (lower-cased) goes against the bridge index of {@link MarkerIndexEntry}
 * entries (code + aliases of every active `domain_entity_types` row).
 *
 * <p>Case rule (spec §4.2):
 * <ul>
 *   <li>If the marker key has the same letter count as the resolved label,
 *       apply <b>per-position case</b>: copy the case of each letter in
 *       the marker key onto the same-index letter in the label.</li>
 *   <li>Otherwise (alias whose length differs from the label), apply the
 *       <b>pattern fallback</b>: all-lower → all-lower label; all-upper →
 *       all-upper label; Title (first upper, rest lower) → Title label;
 *       any other → label rendered as-is.</li>
 * </ul>
 *
 * <p>Security (spec §2.6 R8 / UNIT-19): a `{{var}}` value containing
 * `[[evil]]` must NOT be re-processed. The post-processor runs <i>after</i>
 * i18next's variable interpolation by default, so the guard is: re-read
 * the RAW resource template via {@link i18next.getResource}, collect the
 * markers that existed in the template, and only replace markers whose
 * content matches the raw set. Markers introduced by interpolation never
 * appear in the raw template and are left as literal text.
 */

const MARKER_REGEX = createMarkerRegex()

export const POST_PROCESSOR_NAME = 'entity-noun-markup'
const DEFAULT_NAMESPACE = 'translation'

interface PostProcessOptions extends TOptions {
    lng?: string
    lngs?: string[]
    ns?: string | string[]
    count?: number
}

interface PluralResolverLike {
    getSuffix(lng: string, count: number): string
}

export const entityNounMarkupPostProcessor: PostProcessorModule = {
    type: 'postProcessor',
    name: POST_PROCESSOR_NAME,
    process(value: string, key: string | string[], options: PostProcessOptions): string {
        if (typeof value !== 'string' || !value.includes('[[')) {
            return value
        }
        const lng = pickLocale(options)
        const ns = pickNamespace(options)
        const baseKey = Array.isArray(key) ? key[0] : key
        if (!baseKey) return value
        const resolvedKey = resolveTemplateKey(lng, ns, baseKey, options.count)
        const raw = readRawResource(lng, ns, resolvedKey)
        const rawMarkers = collectRawMarkers(raw)
        // WHY: no markers in the raw template — every `[[…]]` in `value` came from
        // interpolation (a `{{var}}` value carrying the brackets). Leave them.
        if (rawMarkers.size === 0) {
            return value
        }
        return value.replace(MARKER_REGEX, (match, content: string) => {
            if (!rawMarkers.has(content)) {
                return match
            }
            return resolveMarker(content, lng)
        })
    },
}

function pickLocale(options: PostProcessOptions): string {
    if (typeof options.lng === 'string' && options.lng.length > 0) return options.lng
    if (Array.isArray(options.lngs) && options.lngs.length > 0) return options.lngs[0]
    return i18next.language
}

function pickNamespace(options: PostProcessOptions): string {
    if (Array.isArray(options.ns) && options.ns.length > 0) return options.ns[0]
    if (typeof options.ns === 'string' && options.ns.length > 0) return options.ns
    return DEFAULT_NAMESPACE
}

// WHY: i18next hands the post-processor the bare key (e.g. `grants`) even
// when it resolved to a plural variant (`grants_other`) — singular and
// plural templates can carry different markers (`[[access profile]]` vs
// `[[access profiles]]`), so reading the bare key alone would reject the
// rendered marker as "not in raw template". Use i18next's own plural
// resolver to map `(key, count)` to the actual resolved key. (issue 1033 /
// supersedes 1026's PLURAL_SUFFIXES fan-out.)
function resolveTemplateKey(lng: string, ns: string, key: string, count: unknown): string {
    if (typeof count !== 'number') return key
    const resolver = pluralResolver()
    if (!resolver) return key
    let suffix: string
    try {
        suffix = resolver.getSuffix(lng, count)
    } catch (err) {
        void err
        return key
    }
    if (!suffix || key.endsWith(suffix)) return key
    const candidate = `${key}${suffix}`
    return typeof readRawResource(lng, ns, candidate) === 'string' ? candidate : key
}

function pluralResolver(): PluralResolverLike | null {
    const services = (i18next as unknown as { services?: { pluralResolver?: PluralResolverLike } }).services
    return services?.pluralResolver ?? null
}

function readRawResource(lng: string, ns: string, key: string): unknown {
    try {
        return i18next.getResource(lng, ns, key)
    } catch (err) {
        // WHY: i18next.getResource can throw on missing-bundle init races; the
        // caller treats undefined as "no template available" and leaves all
        // markers literal — exactly what we want when the resource is gone.
        void err
        return undefined
    }
}

function collectRawMarkers(raw: unknown): Set<string> {
    const markers = new Set<string>()
    if (typeof raw !== 'string') return markers
    for (const m of raw.matchAll(MARKER_REGEX)) {
        markers.add(m[1])
    }
    return markers
}

function resolveMarker(content: string, lng: string): string {
    if (content.length === 0) return `[[${content}]]`
    const index = getEntityNounMarkerIndex()
    const entry = index.get(content.toLowerCase())
    if (!entry) {
        // WHY: distinguish pre-bootstrap renders (catalogue not yet loaded —
        // 60+ identical-looking warnings per navigation, drown out real
        // issues) from genuine post-bootstrap gaps (a real missing row, worth
        // one warning). The bridge writes a non-empty index once the
        // AppConfig query resolves; rendering before then is expected, not a
        // bug to log. Issue 1046.
        if (import.meta.env?.DEV && index.size > 0) {
            console.warn(`[entity-noun-markup] no catalogue row for marker "[[${content}]]"`)
        }
        return content
    }
    const rawLabel = labelForEntry(entry, lng)
    return applyCase(content, rawLabel)
}

function labelForEntry(entry: MarkerIndexEntry, lng: string): string {
    return entry.translations?.[lng]?.label ?? entry.translations?.en?.label ?? entry.code
}

function applyCase(markerKey: string, label: string): string {
    const markerLetters = markerKey.match(/\p{L}/gu) ?? []
    const labelLetters = label.match(/\p{L}/gu) ?? []
    if (markerLetters.length === labelLetters.length && markerLetters.length > 0) {
        return applyPerPositionCase(markerKey, label)
    }
    return applyPatternFallback(markerKey, label)
}

function applyPerPositionCase(markerKey: string, label: string): string {
    let markerIndex = 0
    const out: string[] = []
    for (const ch of label) {
        if (!isLetter(ch)) {
            out.push(ch)
            continue
        }
        const markerCh = nextLetter(markerKey, markerIndex)
        if (markerCh == null) {
            out.push(ch)
            continue
        }
        out.push(isUpper(markerCh.ch) ? ch.toUpperCase() : ch.toLowerCase())
        markerIndex = markerCh.next
    }
    return out.join('')
}

function nextLetter(s: string, fromIndex: number): {ch: string; next: number} | null {
    for (let i = fromIndex; i < s.length; i++) {
        if (isLetter(s[i])) return {ch: s[i], next: i + 1}
    }
    return null
}

function isLetter(ch: string): boolean {
    return /\p{L}/u.test(ch)
}

function isUpper(ch: string): boolean {
    return ch === ch.toUpperCase() && ch !== ch.toLowerCase()
}

function applyPatternFallback(markerKey: string, label: string): string {
    if (isAllLower(markerKey)) return label.toLowerCase()
    // WHY: Title (first-letter-upper, rest-lower) is checked BEFORE all-upper
    // because a single-letter marker like "C" satisfies both patterns; Title
    // is the conservative pick (matches the user-visible label casing convention).
    // "CAMP" / "CAMPAIGN" fail Title (multiple consecutive caps) → fall through
    // to all-upper, matching UNIT-09's [[CAMP]] → "CAMPAIGN" expectation.
    if (isTitleCase(markerKey)) return toTitle(label)
    if (isAllUpper(markerKey)) return label.toUpperCase()
    return label
}

function isAllLower(s: string): boolean {
    const letters = s.match(/\p{L}/gu) ?? []
    return letters.length > 0 && letters.every((c) => c === c.toLowerCase())
}

function isAllUpper(s: string): boolean {
    const letters = s.match(/\p{L}/gu) ?? []
    return letters.length > 0 && letters.every((c) => c === c.toUpperCase())
}

function isTitleCase(s: string): boolean {
    const letters = s.match(/\p{L}/gu) ?? []
    const first = letters[0]
    if (first == null) return false
    if (!isUpper(first)) return false
    for (let i = 1; i < letters.length; i++) {
        const ch = letters[i]
        if (ch != null && ch !== ch.toLowerCase()) return false
    }
    return true
}

function toTitle(label: string): string {
    const lower = label.toLowerCase()
    for (let i = 0; i < lower.length; i++) {
        if (isLetter(lower[i])) {
            return lower.slice(0, i) + lower[i].toUpperCase() + lower.slice(i + 1)
        }
    }
    return label
}
